/**
 * Catalogue des fournisseurs configurables depuis le studio (écran « Fournisseurs »).
 * Partagé par l'API (validation) et le studio (formulaires) : un champ « secret » n'est jamais relu.
 */

export const PROVIDER_KINDS = ["LLM", "LLM_FALLBACK", "SMS", "WHATSAPP", "PAYMENT_WAVE", "PAYMENT_ORANGE_MONEY", "STT", "PAYMENT_BICTORYS", "TTS", "TELEGRAM"] as const;
export type ProviderKind = (typeof PROVIDER_KINDS)[number];

export type ProviderField = {
  key: string;
  label: string;
  secret?: boolean; // chiffré en base, jamais renvoyé (seulement « ••••a1b2 »)
  required?: boolean;
  type?: "text" | "number" | "url";
  placeholder?: string;
  help?: string;
  pattern?: string; // expression régulière (validation API et formulaire)
  options?: { value: string; label: string }[]; // liste fermée (liste déroulante dans le studio, vérifiée par l'API)
  default?: string | number;
};

export type ProviderDef = { id: string; label: string; fields: ProviderField[]; docsUrl?: string };

const llmFields = (defaultModel: string, baseUrlHelp: string): ProviderField[] => [
  { key: "apiKey", label: "Clé d'API", secret: true, required: true },
  { key: "model", label: "Modèle", required: true, default: defaultModel },
  { key: "baseUrl", label: "Adresse de l'API", type: "url", help: baseUrlHelp },
  { key: "priceInPerMTok", label: "Prix entrée ($ / million de jetons)", type: "number", default: 0, help: "Suivi du coût par élève" },
  { key: "priceOutPerMTok", label: "Prix sortie ($ / million de jetons)", type: "number", default: 0 },
];

/** Champs communs aux voix : langues couvertes et prix (suivi du coût). */
const ttsCommon = (price: number): ProviderField[] => [
  { key: "languages", label: "Langues lues", default: "fr", pattern: "^[a-z]{2}([, ]+[a-z]{2})*$", help: "Codes séparés par des virgules (fr, wo…). Les autres langues passent par la voix du téléphone." },
  { key: "pricePerMChars", label: "Prix ($ / million de caractères)", type: "number", default: price, help: "Suivi du coût par élève (prix public indicatif, à vérifier)." },
];

export const PROVIDER_CATALOG: Record<ProviderKind, { label: string; description: string; providers: ProviderDef[] }> = {
  LLM: {
    label: "Tuteur IA",
    description: "Modèle qui répond aux questions des élèves à partir des leçons validées.",
    providers: [
      { id: "anthropic", label: "Claude (Anthropic)", fields: llmFields("claude-sonnet-5-5", "Laisser vide : https://api.anthropic.com"), docsUrl: "https://docs.claude.com" },
      { id: "openai", label: "Compatible OpenAI (OpenAI, Mistral, Groq, modèle hébergé…)", fields: llmFields("", "Ex. https://api.mistral.ai/v1 — vide : https://api.openai.com/v1") },
    ],
  },
  LLM_FALLBACK: {
    label: "Tuteur IA (secours)",
    description: "Utilisé seulement si le fournisseur principal est en panne (réseau, délai, surcharge).",
    providers: [
      { id: "anthropic", label: "Claude (Anthropic)", fields: llmFields("claude-haiku-5-5", "Laisser vide : https://api.anthropic.com") },
      { id: "openai", label: "Compatible OpenAI", fields: llmFields("", "Ex. https://api.mistral.ai/v1") },
    ],
  },
  SMS: {
    label: "SMS (codes de connexion)",
    description: "Codes à usage unique des parents et des enseignants, et repli quand WhatsApp n'est pas possible.",
    providers: [{
      id: "orange", label: "Orange SMS API", docsUrl: "https://developer.orange.com/apis/sms",
      fields: [
        { key: "clientId", label: "Client ID", secret: true, required: true },
        { key: "clientSecret", label: "Client secret", secret: true, required: true },
        { key: "senderAddress", label: "Numéro expéditeur", required: true, placeholder: "tel:+2210000", pattern: "^tel:\\+\\d{6,15}$" },
        { key: "senderName", label: "Nom affiché (11 caractères max.)", placeholder: "JANGE", pattern: "^[A-Za-z0-9 ]{1,11}$" },
      ],
    }, {
      id: "twilio", label: "Twilio (international)", docsUrl: "https://www.twilio.com/docs/messaging/api/message-resource",
      fields: [
        { key: "accountSid", label: "Account SID", required: true, placeholder: "AC…", pattern: "^AC[0-9a-fA-F]{32}$" },
        { key: "authToken", label: "Auth token", secret: true, required: true },
        { key: "from", label: "Expéditeur", required: true, placeholder: "JANGE, +16414066149 ou MG…", pattern: "^(\\+\\d{6,15}|MG[0-9a-fA-F]{32}|(?=.*[A-Za-z])[A-Za-z0-9 ]{1,11})$", help: "Nom alphanumérique (11 caractères max., ex. JANGE : souvent requis vers l'Afrique, un numéro américain y est refusé), numéro Twilio international, ou Messaging Service (MG…)." },
      ],
    }],
  },
  TELEGRAM: {
    label: "Telegram (codes du studio)",
    description: "Bot qui envoie leurs codes de connexion aux membres de l'équipe qui l'ont relié à leur compte (Mon compte → Telegram). Remplace le SMS pour eux.",
    providers: [{
      id: "telegram-bot", label: "Bot Telegram (BotFather)", docsUrl: "https://core.telegram.org/bots#botfather",
      fields: [{ key: "botToken", label: "Jeton du bot", secret: true, required: true, pattern: "^\\d{6,12}:[A-Za-z0-9_-]{30,}$", help: "Donné par @BotFather après /newbot. « Tester » vérifie le bot et enregistre l'adresse de réception des messages." }],
    }],
  },
  WHATSAPP: {
    label: "WhatsApp (bilans et alertes)",
    description: "Messages modèles « utilité » : bilan du dimanche, début et fin de session, rappels de paiement.",
    providers: [{
      id: "meta", label: "WhatsApp Cloud API (Meta)", docsUrl: "https://developers.facebook.com/docs/whatsapp/cloud-api",
      fields: [
        { key: "accessToken", label: "Jeton d'accès permanent", secret: true, required: true },
        { key: "phoneNumberId", label: "Identifiant du numéro", required: true, pattern: "^\\d{6,20}$" },
        { key: "apiVersion", label: "Version de l'API", default: "v21.0", pattern: "^v\\d+\\.\\d+$" },
      ],
    }],
  },
  PAYMENT_WAVE: {
    label: "Paiement Wave",
    description: "Encaissement des abonnements (Wave Checkout) et confirmation par webhook signé.",
    providers: [{
      id: "wave", label: "Wave Checkout", docsUrl: "https://docs.wave.com/checkout",
      fields: [
        { key: "apiKey", label: "Clé d'API", secret: true, required: true },
        { key: "webhookSecret", label: "Secret du webhook", secret: true, required: true },
      ],
    }],
  },
  STT: {
    label: "Questions à l'oral (transcription)",
    description: "Transforme la question dite à voix haute en texte, que l'élève relit avant de l'envoyer au tuteur. L'audio n'est jamais conservé.",
    providers: [{
      id: "openai", label: "Service compatible OpenAI (Whisper, Groq…)", docsUrl: "https://platform.openai.com/docs/api-reference/audio/createTranscription",
      fields: [
        { key: "apiKey", label: "Clé d'API", secret: true, required: true },
        { key: "model", label: "Modèle", required: true, default: "whisper-1", help: "Ex. whisper-1, gpt-4o-mini-transcribe, whisper-large-v3 (Groq)." },
        { key: "baseUrl", label: "Adresse de l'API", type: "url", default: "https://api.openai.com/v1", help: "Ex. https://api.groq.com/openai/v1" },
      ],
    }],
  },
  TTS: {
    label: "Voix du tuteur (réponses lues)",
    description: "Lit à voix haute les réponses du tuteur avec une voix naturelle. Choisissez le fournisseur, réglez-le, testez, puis activez-le (pour tous les pays ou pour un pays). Sans réglage, ou pour une langue que la voix ne couvre pas, l'app utilise la voix du téléphone (gratuite, en français). Chaque réponse n'est générée qu'une fois (mise en cache).",
    providers: [
      {
        id: "google", label: "Google Cloud Text-to-Speech", docsUrl: "https://cloud.google.com/text-to-speech/docs/voices",
        fields: [
          { key: "apiKey", label: "Clé d'API", secret: true, required: true, help: "Clé Google Cloud restreinte à l'API Text-to-Speech." },
          { key: "voice", label: "Voix", required: true, default: "fr-FR-Standard-A", help: "fr-FR-Standard-A… (4 $ / million de caractères) ; fr-FR-Neural2-A, fr-FR-Wavenet-C… plus naturelles (16 $) : pensez à ajuster le prix." },
          { key: "speakingRate", label: "Vitesse", type: "number", default: 0.95, help: "1 = normale ; 0,9 pour ralentir un peu." },
          ...ttsCommon(4),
        ],
      },
      {
        id: "azure", label: "Microsoft Azure Speech", docsUrl: "https://learn.microsoft.com/azure/ai-services/speech-service/language-support?tabs=tts",
        fields: [
          { key: "apiKey", label: "Clé de la ressource Speech", secret: true, required: true },
          { key: "region", label: "Région", required: true, default: "westeurope", pattern: "^[a-z0-9]{3,30}$", help: "Région de la ressource (ex. westeurope, francecentral)." },
          { key: "voice", label: "Voix", required: true, default: "fr-FR-DeniseNeural", help: "Ex. fr-FR-DeniseNeural, fr-FR-HenriNeural, fr-FR-VivienneMultilingualNeural." },
          { key: "rate", label: "Vitesse", default: "-5%", help: "Ex. -10%, 0%, +5%." },
          ...ttsCommon(15),
        ],
      },
      {
        id: "polly", label: "Amazon Polly", docsUrl: "https://docs.aws.amazon.com/polly/latest/dg/available-voices.html",
        fields: [
          { key: "accessKeyId", label: "Identifiant de clé d'accès", required: true },
          { key: "secretAccessKey", label: "Clé d'accès secrète", secret: true, required: true },
          { key: "region", label: "Région", required: true, default: "eu-west-3", pattern: "^[a-z]{2}-[a-z]+-\\d$" },
          { key: "voice", label: "Voix", required: true, default: "Lea", help: "Ex. Lea, Remi (neural), Celine, Mathieu (standard)." },
          { key: "engine", label: "Moteur", default: "neural", pattern: "^(standard|neural|generative|long-form)$", help: "standard (le moins cher) ou neural." },
          ...ttsCommon(16),
        ],
      },
      {
        id: "openai", label: "OpenAI (ou service compatible)", docsUrl: "https://platform.openai.com/docs/api-reference/audio/createSpeech",
        fields: [
          { key: "apiKey", label: "Clé d'API", secret: true, required: true },
          { key: "model", label: "Modèle", required: true, default: "gpt-4o-mini-tts", help: "Ex. gpt-4o-mini-tts, tts-1." },
          { key: "voice", label: "Voix", required: true, default: "nova", help: "Ex. alloy, nova, shimmer." },
          { key: "baseUrl", label: "Adresse de l'API", type: "url", default: "https://api.openai.com/v1" },
          ...ttsCommon(15),
        ],
      },
      {
        id: "elevenlabs", label: "ElevenLabs", docsUrl: "https://elevenlabs.io/docs/api-reference/text-to-speech",
        fields: [
          { key: "apiKey", label: "Clé d'API", secret: true, required: true },
          { key: "voice", label: "Identifiant de la voix", required: true, help: "Identifiant copié depuis la bibliothèque de voix ElevenLabs." },
          { key: "model", label: "Modèle", default: "eleven_multilingual_v2", help: "Ex. eleven_multilingual_v2, eleven_flash_v2_5 (moins cher)." },
          ...ttsCommon(50),
        ],
      },
      {
        id: "selfhosted", label: "Voix auto-hébergée (Pocket-TTS, compatible OpenAI)", docsUrl: "https://github.com/kyutai-labs/pocket-tts",
        fields: [
          { key: "baseUrl", label: "Adresse du serveur", type: "url", required: true, placeholder: "https://voix.exemple.sn/v1", help: "Serveur qui expose /v1/audio/speech (infra/voix-pocket)." },
          { key: "apiKey", label: "Clé (facultative)", secret: true },
          { key: "model", label: "Modèle", required: true, default: "pocket-tts" },
          {
            key: "voice", label: "Voix", required: true, default: "estelle", help: "Catalogue de Pocket-TTS (infra/voix-pocket) : écouter avant d'activer.",
            options: [
              { value: "estelle", label: "estelle — femme, français (par défaut)" },
              { value: "marius", label: "marius — homme, français" },
              { value: "javert", label: "javert — homme, français" },
              { value: "charles", label: "charles — homme, anglais" },
              { value: "paul", label: "paul — homme, anglais" },
              { value: "george", label: "george — homme, anglais" },
              { value: "michael", label: "michael — homme, anglais" },
              { value: "jean", label: "jean — homme, anglais" },
              { value: "stuart_bell", label: "stuart_bell — homme, anglais" },
              { value: "rafael", label: "rafael — homme, portugais" },
              { value: "juergen", label: "juergen — homme, allemand" },
              { value: "giovanni", label: "giovanni — homme, italien" },
              { value: "alba", label: "alba — femme, anglais" },
              { value: "anna", label: "anna — femme, anglais" },
              { value: "eve", label: "eve — femme, anglais" },
              { value: "jane", label: "jane — femme, anglais" },
              { value: "mary", label: "mary — femme, anglais" },
              { value: "vera", label: "vera — femme, anglais" },
              { value: "cosette", label: "cosette — femme, anglais" },
              { value: "lola", label: "lola — femme, espagnol" },
            ],
          },
          ...ttsCommon(0),
        ],
      },
    ],
  },
  PAYMENT_BICTORYS: {
    label: "Paiement Bictorys",
    description: "Agrégateur : le parent choisit Wave, Orange Money, Free Money, Max it ou carte sur la page Bictorys. Paiement confirmé par webhook authentifié, puis vérifié auprès de Bictorys.",
    providers: [{
      id: "bictorys", label: "Bictorys", docsUrl: "https://docs.bictorys.com/docs/integration",
      fields: [
        { key: "publicKey", label: "Clé publique (public-… ou test_public-…)", secret: true, required: true },
        { key: "webhookSecret", label: "Secret du webhook (Développeurs → Webhooks)", secret: true, required: true },
        { key: "baseUrl", label: "Adresse de l'API", type: "url", required: true, default: "https://api.bictorys.com", help: "https://api.test.bictorys.com pour le bac à sable." },
        { key: "paymentType", label: "Moyen imposé (facultatif)", pattern: "^(|wave_money|orange_money|maxit|free_money|card)$", help: "Vide : le parent choisit sur la page Bictorys. Sinon : wave_money, orange_money, maxit, free_money ou card." },
      ],
    }],
  },
  PAYMENT_ORANGE_MONEY: {
    label: "Paiement Orange Money",
    description: "Encaissement des abonnements (Orange Money Web Payment). Chaque paiement est confirmé auprès d'Orange avant d'être validé.",
    providers: [{
      id: "orange_webpay", label: "Orange Money Web Payment", docsUrl: "https://developer.orange.com/apis/om-webpay",
      fields: [
        { key: "clientId", label: "Client ID (Orange Developer)", secret: true, required: true },
        { key: "clientSecret", label: "Client secret", secret: true, required: true },
        { key: "merchantKey", label: "Clé marchand (merchant_key)", secret: true, required: true },
        { key: "countrySegment", label: "Segment pays de l'API", required: true, default: "dev", pattern: "^[a-z]{2,4}$", help: "« dev » pour le bac à sable ; en production, le code donné par Orange pour le pays." },
        { key: "currency", label: "Devise", required: true, default: "OUV", pattern: "^[A-Z]{3}$", help: "OUV en bac à sable, XOF en production." },
        { key: "baseUrl", label: "Adresse de l'API", type: "url", default: "https://api.orange.com", help: "À ne changer que sur indication d'Orange." },
      ],
    }],
  },
};

/** « * » = tous les pays ; un réglage propre à un pays (« SN ») passe devant. */
export const ALL_COUNTRIES = "*";

// ---------------------------------------------------------------------------
// Rôles du studio
// ---------------------------------------------------------------------------

export const STUDIO_ROLES = ["AUTHOR", "SUBJECT_REVIEWER", "LANGUAGE_REVIEWER", "EDITOR", "ADMIN", "TECH"] as const;
export type StudioRole = (typeof STUDIO_ROLES)[number];

export const ROLE_INFO: Record<StudioRole, { label: string; family: "content" | "system"; description: string }> = {
  AUTHOR: { label: "Auteur", family: "content", description: "Rédige notions, leçons et exercices." },
  SUBJECT_REVIEWER: { label: "Relecteur matière", family: "content", description: "Valide le contenu scientifique (français)." },
  LANGUAGE_REVIEWER: { label: "Relecteur langue", family: "content", description: "Valide une langue locale et le glossaire." },
  EDITOR: { label: "Éditeur", family: "content", description: "Gère programmes, annales et publication." },
  ADMIN: { label: "Administrateur système", family: "system", description: "Clés des fournisseurs, équipe et rôles. Double authentification obligatoire." },
  TECH: { label: "Suivi technique", family: "system", description: "État des fournisseurs et de l'API, tests de connexion. Ne voit jamais les clés." },
};

/** Ce que chaque rôle peut faire dans la partie système. Les collaborateurs contenu n'y ont aucun accès. */
export const PERMISSIONS = {
  "system.view": ["ADMIN", "TECH"],
  "providers.view": ["ADMIN", "TECH"],
  "providers.test": ["ADMIN", "TECH"],
  "providers.edit": ["ADMIN"],
  "team.view": ["ADMIN"],
  "team.manage": ["ADMIN"],
  "countries.manage": ["ADMIN"],
  /** Référentiel commun aux programmes : années scolaires et matières. */
  "reference.manage": ["ADMIN"],
  /** Import en série de la banque 3D (entrées du manifeste seulement : aucun fichier arbitraire). */
  "bank.manage": ["ADMIN", "TECH"],
} as const satisfies Record<string, readonly StudioRole[]>;
export type Permission = keyof typeof PERMISSIONS;

export const can = (roles: readonly string[], p: Permission) => (PERMISSIONS[p] as readonly string[]).some((r) => roles.includes(r));
/** Rôles qui exigent la double authentification (TOTP) avant tout accès à la partie système. */
export const ROLES_REQUIRING_2FA: readonly StudioRole[] = ["ADMIN", "TECH"];
